import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import BotsCarouselMore from './botsCarouselMore/BotsCarouselMore';
import classes from './BotsCarousel.module.css';

export default function BotsCarousel({
  items = [],
  activeId,
  onSelect,
  getItemKey,
  getItemLabel,
  renderItem,
  className = '',
  itemClassName = '',
  moreClassName = '',
  listClassName = '',
  dividerClassName = '',
}) {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const firstItemRef = useRef(null);
  const rafRef = useRef(null);
  const [orderedItems, setOrderedItems] = useState(items);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useEffect(() => {
    setOrderedItems(items);
    setVisibleCount(items.length);
  }, [items]);

  const refreshLayout = useCallback(() => {
    const listEl = listRef.current;
    const firstItemEl = firstItemRef.current;
    if (orderedItems.length === 0) {
      setVisibleCount(0);
      return;
    }
    if (!listEl || !firstItemEl) return;
    const listWidth = listEl.clientWidth;
    const itemWidth = firstItemEl.offsetWidth;
    if (itemWidth <= 0) return;
    const styles = window.getComputedStyle(listEl);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const nextCount = Math.floor((listWidth + gap) / (itemWidth + gap));
    const normalizedCount = Math.min(orderedItems.length, Math.max(1, nextCount));
    setVisibleCount((current) => (current === normalizedCount ? current : normalizedCount));
  }, [orderedItems.length]);

  const scheduleRefresh = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      refreshLayout();
    });
  }, [refreshLayout]);

  useLayoutEffect(() => {
    scheduleRefresh();
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [scheduleRefresh, orderedItems]);

  useEffect(() => {
    const root = rootRef.current;
    const list = listRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => scheduleRefresh());
    observer.observe(root);
    if (list) observer.observe(list);
    if (firstItemRef.current) observer.observe(firstItemRef.current);
    return () => observer.disconnect();
  }, [scheduleRefresh, orderedItems.length]);

  if (typeof renderItem !== 'function') return null;

  const hiddenCount = Math.max(0, orderedItems.length - visibleCount);
  const hiddenNames = getItemLabel ?
    orderedItems.slice(visibleCount).map(getItemLabel).filter(Boolean)
    : [];

  const handleRotate = () => {
    setOrderedItems((prev) => {
      if (prev.length <= 1) return prev;
      const [first, ...rest] = prev;
      return [...rest, first];
    });
  };

  return (
    <div
      ref={rootRef}
      className={[classes.Carousel, className].filter(Boolean).join(' ')}
      dir="rtl"
    >
      <ul
        ref={listRef}
        className={[classes.List, listClassName].filter(Boolean).join(' ')}
      >
        {orderedItems.map((item, index) => {
          const key = getItemKey(item);
          const isActive = activeId != null && key === activeId;
          return (
            <li
              key={key}
              ref={index === 0 ? firstItemRef : undefined}
              className={classes.ListItem}
              hidden={index >= visibleCount}
            >
              {index > 0 ?
                <span
                  className={[classes.Divider, dividerClassName].filter(Boolean).join(' ')}
                  aria-hidden="true"
                />
                : null}
              <div className={[classes.ItemSlot, itemClassName].filter(Boolean).join(' ')}>
                {renderItem({
                  item,
                  index,
                  isActive,
                  onSelect: () => onSelect?.(item),
                })}
              </div>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 ?
        <BotsCarouselMore
          className={moreClassName}
          count={hiddenCount}
          hiddenNames={hiddenNames}
          onClick={handleRotate}
        />
        : null}
    </div>
  );
}
